import React, {  useContext, useEffect, useRef, useState } from 'react';
import Uppy from '@uppy/core';
import { Dashboard } from '@uppy/react';
import AwsS3 from '@uppy/aws-s3';
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import { AppBar, Box, Checkbox, FormControlLabel, Grid, MenuItem, Paper, Tab, Tabs, TextField, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import mixpanel from 'mixpanel-browser';

import ComputerIcon from '@material-ui/icons/ComputerSharp';
import { S3Icon, WeTransferIcon } from './Icons';

import { useAxios } from '../utils/axiosApi';
import { onError } from '../utils/errorLib';
import { useStableCallback } from '../utils/hooksLib';
import { isValidS3Link, isValidS3BucketLink, isValidS3PathLink, isValidWeTransferLink } from '../utils/validationLib';
import { useAuth } from '../utils/useAuth';

import { TextureTypes, AllTextureTypes, makeCleanFilename, splitExt, textureTypeFromFilename } from '../utils/shared/formatLib';
import { ScanType, ScanTypeFileExtensions } from '../utils/shared/metadataLib';

import { AppContext } from './AppState';
import TabPanel from './TabPanel';

const allowedFileTypes = ScanTypeFileExtensions;

const tabHeight = '48px';

export const UploadMethod = Object.freeze({
  UPPY: 0,
  WE_TRANSFER: 1,
  S3: 2,
});
export const UploadMethodNames = Object.freeze({
  UPPY: 'Uppy',
  WE_TRANSFER: 'WeTransfer',
  S3: 'S3',
});


const useStyles = makeStyles((theme) => ({
  captionDiv: {
    fontSize: '0.8em',
    color: '#777',
    textAlign: 'left',
  },
  captionEmailText: {
    color: theme.palette.secondary.main,
    cursor: 'pointer',
    marginLeft: theme.spacing(0.5),
    textAlign: 'center',
    textDecoration: 'none',
    '&:hover': {
      color: '#003fff',
    },
  },
  uploadPanel: {
    marginTop: '1em',
    marginBottom: '1em',
    marginLeft: '0.5em',
  },
  tabs: {
    backgroundColor: theme.palette.accent.main,
    boxShadow: 'none',
  },
  indicator :{
    // top: '0px',
  },
  tab: {
    height: tabHeight,
    minWidth: 100,
  },
  textureTypes: {
    display: 'flex',
    alignItems: 'center',
  },
  textureTypesHeadings: {
    marginBottom: '1em',
  },
  filename: {
    wordWrap: 'break-word',
  },
  errorMargin: {
    marginTop: theme.spacing(5),
  },
}));

const USE_MULTIPART_PLUGIN = true;

function PhotoUpload(props) {
  const {
    scanType,
    userId,
    userItem,
    materialId, setMaterialId,
    setUploadComplete,
    name, setName,
    setPhotos,
    originalPhotos, setOriginalPhotos,
    importFilenames, setImportFilenames,
    updateImportFilenames,
    duplicateMapTypes,
    mapTypeErrors,
    onUploading,
    uploadMethod, setUploadMethod,
    uploadLinks, setUploadLinks,
  } = props;

  const { apiGet, apiPost, apiDelete } = useAxios();

  const classes = useStyles();
  const { showAlert } = useContext(AppContext);
  const { isAuthenticated } = useAuth();
  const filesAddedCount = useRef(0);
  const [filesCompleted, setFilesCompleted] = useState([]);
  const [hasUppyUploadStarted, setHasUppyUploadStarted] = useState(false);
  const [uppyUploadComplete, setUppyUploadComplete] = useState(false);
  const [uploadPathsLoaded, setUploadPathsLoaded] = useState(false);
  const [processWholeBucket, setProcessWholeBucket] = useState(false);

  //
  // Uppy uploader
  //

  const [uppy] = useState(() =>
    new Uppy({ id: scanType })
      // shouldUseMultipart must be set during the initial use() call.
      .use(AwsS3, { shouldUseMultipart: USE_MULTIPART_PLUGIN })
  );

  const onFileAddedEvent = useStableCallback((file) => {
    if (scanType === ScanType.TEXTURE_IMPORT) {
      const [mapType, prefix] = textureTypeFromFilename(file.data.name);
      setImportFilenames(
        ({ items }) => ({ items: items.set(file.data.name, mapType) })
      );
      if (!name) {
        setName(prefix);
      }
    }
  }, [scanType, setImportFilenames]);

  const onFileRemovedEvent = useStableCallback((file, reason) => {
    if (scanType === ScanType.TEXTURE_IMPORT) {
      setImportFilenames(
        ({ items }) => {
          items.delete(file.data.name);
          return { items };
        }
      );
      setFilesCompleted(oldFiles => oldFiles.filter((_, i) => originalPhotos[i]?.name !== file.data.name));
      setPhotos(oldPhotos => oldPhotos.filter((_, i) => originalPhotos[i]?.name !== file.data.name));
      setOriginalPhotos(oldOriginalPhotos => oldOriginalPhotos.filter(op => op.name !== file.data.name));
    }
  }, [scanType, setImportFilenames, originalPhotos, setFilesCompleted, setPhotos, setOriginalPhotos]);

  const onUploadEvent = useStableCallback((data) => {
    console.debug(`Starting photo upload ${data.id} for user ${userId} with files ${data.fileIDs}`);
    mixpanel.track('Start Scan Upload', {
      'Material ID': materialId,
      'Scan Type': scanType,
      'Upload Method': UploadMethodNames.UPPY,
      'Upload ID': data.id,
      'Photos': data.fileIDs.length,
    });
    setUppyUploadComplete(false);
    setHasUppyUploadStarted(true);
    if (onUploading) {
      onUploading(true);
    }
  }, [onUploading, userId]);

  const onUploadSuccessEvent = useStableCallback((file, response) => {
    //console.debug('adding completed photo:', file.name);
    var ix = null; // Index to replace, if any
    if (originalPhotos && originalPhotos.filter(op => op.name === file.data.name).length > 0) {
      console.log('Upload filename already present, replacing: ', file.data.name)
      ix = originalPhotos.findIndex(op => op.name === file.data.name);
    }
    setFilesCompleted(oldFiles => [
      ...(oldFiles.filter((_v, i) => i !== ix)),
      file.name,
    ]);
    setPhotos(oldPhotos => [
      ...(oldPhotos.filter((_v, i) => i !== ix)),
      file.name,
    ]);
    setOriginalPhotos(oldOriginalPhotos => [
      ...(oldOriginalPhotos.filter((_v, i) => i !== ix)),
      { name: file.data.name, relativePath: file.data.relativePath },
    ]);
  }, [setFilesCompleted, originalPhotos, setOriginalPhotos, setPhotos]);

  const onCompleteEvent = useStableCallback((result) => {
    mixpanel.track('Complete Scan Upload', {
      'Material ID': materialId,
      'Scan Type': scanType,
      'Upload Method': UploadMethodNames.UPPY,
      'Succeeded Count': filesCompleted.length,
      'Failed Count': result.failed.length,
      'Succeeded Size Total MB': filesCompleted.reduce((n, { size }) => n + size, 0)/1e6,
      'Failed Size Total MB': result.failed.reduce((n, { size }) => n + size, 0)/1e6,
    });
    console.debug('upload complete');
    console.debug('successful files:', filesCompleted);
    console.debug('failed files:', result.failed);
    setUppyUploadComplete(true);
  }, [filesCompleted]);

  // AWS S3 standard callbacks for Uppy
  const getUploadParameters = useStableCallback(async (file, { signal }) => {
    console.assert(materialId, JSON.stringify({ materialId, message: 'materialId should be truthy'}));

    var response = {};
    try {
      response = await apiPost('/upload', { filename: file.name, scanType }, {
        params: { userId, materialId },
        signal,
      });
    } catch (e) {
      onError(e);
      throw e;
    }
    if (response.data?.status == 'success') {
      const { method, url, fields } = response.data.data;
      const headers = { 'Content-Type': file.type };  // Content type header required by S3
      return { method, url, fields, headers };
    } else {
      showAlert({ status: 'error', message: 'An error occurred during upload' });
      return null;
    }
  }, [apiPost, materialId, scanType, showAlert, userId]);

  // AWS S3 multipart callbacks for Uppy
  const createMultipartUpload = useStableCallback(async (file, signal) => {
    console.assert(materialId, JSON.stringify({ materialId, message: 'materialId should be truthy'}));

    let response = {};
    try {
      response = await apiPost('/companion/s3/multipart',
      {
        filename: file.name,
        type: file.type,
        metadata: {},
      },
      {
        params: { userId, materialId },
        signal,
      });
    } catch (e) {
      onError(e);
      throw e;
    }
    if (response.data?.status == 'success') {
      return response.data.data;
    } else {
      showAlert({ status: 'error', message: 'An error occurred during upload' });
      return null;
    }
  }, [apiPost, materialId, showAlert, userId]);

  const listParts = useStableCallback(async (file, { uploadId, key }, signal) => {
    console.assert(materialId, JSON.stringify({ materialId, message: 'materialId should be truthy'}));

    let response = {};
    try {
      const uploadIdEnc = encodeURIComponent(uploadId);
      response = await apiGet(`/companion/s3/multipart/${uploadIdEnc}`, {
        params: { userId, materialId, key },
        signal,
      });
    } catch (e) {
      onError(e);
      throw e;
    }
    if (response.data?.status == 'success') {
      return response.data.data;
    } else {
      showAlert({ status: 'error', message: 'An error occurred during upload' });
      return null;
    }
  }, [apiGet, materialId, showAlert, userId]);

  const signPart = useStableCallback(async (file, { uploadId, key, partNumber, signal } ) => {
    console.assert(materialId, JSON.stringify({ materialId, message: 'materialId should be truthy'}));

    let response = {};
    const uploadIdEnc = encodeURIComponent(uploadId);
    const partNumberEnc = encodeURIComponent(partNumber);
    try {
      response = await apiGet(`/companion/s3/multipart/${uploadIdEnc}/${partNumberEnc}`, {
        params: { userId, materialId, key },
        signal,
      });
    } catch (e) {
      onError(e);
      throw e;
    }
    if (response.data?.status == 'success') {
      return response.data.data;
    } else {
      showAlert({ status: 'error', message: 'An error occurred during upload' });
      return null;
    }
  }, [apiGet, materialId, showAlert, userId]);

  const abortMultipartUpload = useStableCallback(async (file, { key, uploadId }, signal) => {
    console.assert(materialId, JSON.stringify({ materialId, message: 'materialId should be truthy'}));

    let response = {};
    const uploadIdEnc = encodeURIComponent(uploadId);
    try {
      response = await apiDelete(`/companion/s3/multipart/${uploadIdEnc}`, {
        params: { userId, materialId, key },
        signal,
      });
    } catch (e) {
      onError(e);
      throw e;
    }
    if (response.data?.status == 'success') {
      return response.data.data;
    } else {
      showAlert({ status: 'error', message: 'An error occurred during upload' });
      return null;
    }
  }, [apiDelete, materialId, showAlert, userId]);

  const completeMultipartUpload = useStableCallback(async (file, { key, uploadId, parts }, signal) => {
    console.assert(materialId, JSON.stringify({ materialId, message: 'materialId should be truthy'}));

    let response = {};
    const uploadIdEnc = encodeURIComponent(uploadId);
    try {
      response = await apiPost(`/companion/s3/multipart/${uploadIdEnc}/complete`, { parts }, {
        params: { userId, materialId, key },
        signal,
      });
    } catch (e) {
      onError(e);
      throw e;
    }
    if (response.data?.status == 'success') {
      return response.data.data;
    } else {
      showAlert({ status: 'error', message: 'An error occurred during upload' });
      return null;
    }
  }, [apiPost, materialId, showAlert, userId]);

  const restrictions = { allowedFileTypes: allowedFileTypes[scanType] };
  if (scanType === ScanType.TEXTURE_IMPORT) {
    restrictions.maxNumberOfFiles = TextureTypes.size;
  }

  useEffect(() => {
    uppy.setOptions({

      onBeforeFileAdded: (currentFile, files) => {
        // Check if a file with the same content has already been added.
        // This is the same check that Uppy does by default, but we need to do it explicitly when we use our
        // own handler.
        if (Object.hasOwn(files, currentFile.id)) {
            console.debug(`File ID for ${currentFile.name} is already in the upload list`);
            uppy.log(`File ID for ${currentFile.name} is already in the upload list`, 'warn');
            // No need to show an info message here, because Uppy already does that.
          return false;
        }

        // Check if a file with the same name has already been added. In principle we can handle this but
        // currently the UI for selecting texture types doesn't support it.
        const filenames = Object.values(files).map(file => file.data.name);
        if (filenames.includes(currentFile.name)) {
          console.debug(`File name ${currentFile.name} is already in the upload list`);
          uppy.log(`File name ${currentFile.name} is already in the upload list`, 'warn');
          uppy.info(`Cannot add the file '${currentFile.name}', name already exists`, 'warn', 5000);
          return false;
        }

        const cleanFilename = makeCleanFilename(currentFile.name);
        const [ root, ext ] = splitExt(cleanFilename);
        // We assume this incrementing logic is safe from race conditions, because the calling function inside
        // Uppy modifies the files object after our callback returns which implies that Uppy has considered
        // and prevented race condition in this code path.
        filesAddedCount.current += 1;
        const currentFileIndex = filesAddedCount.current - 1;
        const modifiedFilename = `${root}__${String(currentFileIndex).padStart(3, '0')}${ext}`;
        const modifiedFile = {
          ...currentFile,
          name: modifiedFilename,
        };
        return modifiedFile;
      },
      restrictions,
    });

    // When shouldUseMultipart is set to true, the AwsS3 plugin renames itself to AwsS3Multipart.
    uppy.getPlugin('AwsS3Multipart').setOptions({
      getUploadParameters,
      createMultipartUpload,
      listParts,
      signPart,
      abortMultipartUpload,
      completeMultipartUpload,
    });

    uppy.on('file-added', onFileAddedEvent);
    uppy.on('file-removed', onFileRemovedEvent);
    uppy.on('upload', onUploadEvent);
    uppy.on('upload-success', onUploadSuccessEvent);
    uppy.on('complete', onCompleteEvent);

    return () => {
      uppy.off('file-added', onFileAddedEvent);
      uppy.off('file-removed', onFileRemovedEvent);
      uppy.off('upload', onUploadEvent);
      uppy.off('upload-success', onUploadSuccessEvent);
      uppy.off('complete', onCompleteEvent);
      uppy.cancelAll();
      uppy.clearUploadedFiles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    if (!materialId) {
      (async function () {
        var response;
        // Create a unique contextId for this component instance and pass this in the query string to avoid
        // browser or network caching the uploadGet API response and therefore avoid reusing the same
        // materialId for multiple uploads.
        let contextId = self.crypto.randomUUID();
        try {
          response = await apiGet('/upload', {
            params: { userId, contextId }
          });
        } catch (e) {
          onError(e);
        }
        const { status, data } = response?.data;
        if (status == 'success') {
          console.log('New materialId:', data.materialId, 'contextId:', contextId);
          setMaterialId(data.materialId);
        } else {
          showAlert({ status: 'error', message: 'An error occurred while preparing for upload' });
        }
      }) ();
    }
  }, [apiGet, isAuthenticated, materialId, setMaterialId, showAlert, userId]);

  const fileFormatList = allowedFileTypes[scanType].map(type => type.replace(/^\./, '')).join(', ');

  useEffect(() => {
    setUploadComplete(
      (uploadMethod === UploadMethod.UPPY && uppyUploadComplete) ||
      (uploadMethod === UploadMethod.WE_TRANSFER && isValidWeTransferLink(uploadLinks[uploadMethod])) ||
      (uploadMethod === UploadMethod.S3 && (
        isValidS3Link(userItem, uploadLinks[uploadMethod]) && (
          processWholeBucket && isValidS3BucketLink(userItem, uploadLinks[uploadMethod]) ||
          !processWholeBucket && isValidS3PathLink(userItem, uploadLinks[uploadMethod])
        )
      ))
    );
  }, [processWholeBucket, setUploadComplete, uploadLinks, uploadMethod, uppyUploadComplete, userItem]);

  useEffect(() => {
    if (!uploadPathsLoaded && userItem?.uploadPaths) {
      let s3UploadLinkInitial = userItem.uploadPaths[0];
      s3UploadLinkInitial = s3UploadLinkInitial.endsWith('/') ? s3UploadLinkInitial : s3UploadLinkInitial + '/';
      setUploadLinks({...uploadLinks, [UploadMethod.S3]: s3UploadLinkInitial });
      setUploadPathsLoaded(true);
    }
  }, [setUploadLinks, uploadLinks, uploadPathsLoaded, userItem]);

  const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
  return (<>
    <div>
      {materialId && (
        <>
          <Paper className={classes.uploadPanel} elevation={0} variant='outlined'>
            <AppBar position='static' className={classes.tabs}>
              <Tabs
                classes={{
                  indicator: classes.indicator,
                }}
                value={uploadMethod}
                onChange={(event, newValue) => {setUploadMethod(newValue)}}
                indicatorColor='secondary'
                textColor='secondary'
                variant='fullWidth'
              >
                <Tooltip title='Upload files direct from your computer'>
                  <Tab
                    value={UploadMethod.UPPY}
                    className={classes.tab}
                    icon={<ComputerIcon />}
                    />
                </Tooltip>
                <Tooltip title='Batch upload files via WeTransfer'>
                  <Tab
                    value={UploadMethod.WE_TRANSFER}
                    className={classes.tab}
                    icon={<WeTransferIcon />}
                    disabled={hasUppyUploadStarted}
                    />
                </Tooltip>
                {userItem?.uploadPaths &&
                  <Tooltip title='Batch upload files via S3'>
                    <Tab
                      value={UploadMethod.S3}
                      className={classes.tab}
                      icon={<S3Icon />}
                      disabled={hasUppyUploadStarted}
                    />
                  </Tooltip>
                }
              </Tabs>
            </AppBar>
            <TabPanel value={uploadMethod} index={UploadMethod.UPPY}>
              <Grid container spacing={3}>
                <Grid item xs={scanType === ScanType.CAMERA_SCAN ? 12 : 6}>
                  <Dashboard
                    uppy={uppy}
                    showLinkToFileUploadResult={false}
                    showProgressDetails={true}
                    doneButtonHandler={null}
                    proudlyDisplayPoweredByUppy={false}
                    disableThumbnailGenerator={scanType === ScanType.CAMERA_SCAN}
                    width={'100%'}
                    height={'40vh'}
                  />
                  <style>
                    {`.uppy-Dashboard-inner {
                        z-index: 0 !important
                    }`}
                  </style>
                </Grid>
                {scanType === ScanType.TEXTURE_IMPORT &&
                  <Grid item xs={6}>
                    <Grid container className={classes.textureTypes}>
                      <Grid item xs={8} className={classes.textureTypesHeadings}>
                        <Typography>
                          <b>
                            Filename
                          </b>
                        </Typography>
                      </Grid>
                      <Grid item xs={4} className={classes.textureTypesHeadings}>
                        <Typography>
                          <b>
                            Texture type
                          </b>
                        </Typography>
                      </Grid>
                      {[...importFilenames.items].sort(collator.compare).map(([filename, mapType], index) => (
                        <React.Fragment key={index}>
                          <Grid item xs={8} className={classes.filename}>
                            <Typography>
                              {filename}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              select
                              value={mapType}
                              error={!mapType || duplicateMapTypes.includes(mapType)}
                              onChange={(event) => updateImportFilenames(filename, event.target.value)}
                            >
                              {AllTextureTypes.map((name) => (
                                <MenuItem value={name} key={name}>
                                  {name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </React.Fragment>
                      ))}
                      {mapTypeErrors &&
                        <Grid item xs={12} className={classes.errorMargin}>
                          <Typography color='error' component={'div'}>
                            {mapTypeErrors.map((error) => (
                              <div key={error}>
                                {error}
                              </div>
                            ))}
                          </Typography>
                        </Grid>
                      }
                    </Grid>
                  </Grid>
                }
              </Grid>
            </TabPanel>
            <TabPanel value={uploadMethod} index={UploadMethod.WE_TRANSFER}>
              <Box height='25em'>
                <Typography component='span'>
                  You can upload multiple {scanType === ScanType.CAMERA_SCAN ? 'scans' : 'materials'} using <a href='https://bandicoot.wetransfer.com' target='_blank'>WeTransfer</a>:
                  <ol>
                    <li key='1'>Organise your {scanType === ScanType.CAMERA_SCAN ? 'photos' : 'materials'} into folders, with a single {scanType === ScanType.CAMERA_SCAN ? 'scan' : 'set of texture images'} in each folder</li>
                    <li key='2'>Go to the <a href='https://bandicoot.wetransfer.com' target='_blank'>Bandicoot WeTransfer page</a> and drag in your {scanType === ScanType.CAMERA_SCAN ? 'scan' : 'texture'} folders (up to 200 GB per transfer)</li>
                    <li key='3'>Get a link and then come back to this page and paste it in here</li>
                  </ol>
                  <Grid container>
                    <Grid item xs={12}>
                      <TextField
                        variant='filled'
                        fullWidth
                        margin='normal'
                        label='WeTransfer link'
                        onChange={(e) => setUploadLinks({...uploadLinks, [uploadMethod]: e.target.value})}
                        value={uploadLinks[uploadMethod]}
                        required={true}
                        />
                    </Grid>
                  </Grid>
                </Typography>
              </Box>
            </TabPanel>
            {userItem?.uploadPaths &&
              <TabPanel value={uploadMethod} index={UploadMethod.S3}>
                <Box height='25em'>
                  <Typography component='span'>
                    You can upload multiple {scanType === ScanType.CAMERA_SCAN ? 'scans' : 'materials'} using S3:
                    <ol>
                      <li key='1'>Organise your {scanType === ScanType.CAMERA_SCAN ? 'photos' : 'materials'} into folders, with a single {scanType === ScanType.CAMERA_SCAN ? 'scan' : 'set of texture images'} in each folder</li>
                      <li key='2'>Add a new batch folder on AWS S3 using details supplied by Bandicoot</li>
                      <li key='3'>Upload your {scanType === ScanType.CAMERA_SCAN ? 'scan' : 'texture'} folders into the batch folder on AWS S3</li>
                      <li key='4'>Copy the S3 path to the batch folder then come back to this page and paste it in here</li>
                    </ol>
                  </Typography>
                  <Grid container>
                    <Grid item xs={12}>
                      <TextField
                        variant='filled'
                        fullWidth
                        margin='normal'
                        label='S3 path'
                        onChange={(e) => setUploadLinks({...uploadLinks, [uploadMethod]: e.target.value})}
                        value={uploadLinks[uploadMethod]}
                        required={true}
                      />
                    </Grid>
                  </Grid>
                  {isValidS3BucketLink(userItem, uploadLinks[uploadMethod]) &&
                    <FormControlLabel
                      control={<Checkbox
                        checked={processWholeBucket}
                        onChange={(e) => setProcessWholeBucket(e.target.checked)}
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                        color={'primary'}
                        disabled={!isValidS3BucketLink(userItem, uploadLinks[uploadMethod])}
                      />}
                      label={'Process all ' + (scanType === ScanType.CAMERA_SCAN ? 'scans' : 'materials') + ' in bucket'}
                    />
                  }
                </Box>
              </TabPanel>
            }
          </Paper>
        </>
      )}
      <div className={classes.captionDiv}>
        {scanType === ScanType.CAMERA_SCAN ? (<>
          <Typography variant='caption'>
            Camera RAW files only ({fileFormatList}). To use a different RAW format,
            <a href='https://www.bandicootimaging.com/contact/contact'
              className={classes.captionEmailText}
            >
              contact us
            </a>
          </Typography>
        </>) : (
          <Typography variant='caption'>
            PNG, JPG, EXR or TIFF texture images
          </Typography>
        )}
      </div>
    </div>
  </>);
}

export default PhotoUpload;
